import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { usePromiseTracker } from 'react-promise-tracker'

import {
  Grid, Dialog, List, ListItem, Button,
  DialogTitle as MuiDialogTitle, Typography,
  IconButton, DialogContent as MuiDialogContent,
  Tooltip, Paper, CircularProgress
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'

import { makeStyles, withStyles } from '@material-ui/core/styles'
import { setFilter, setSearchModal } from '../../../redux/actions/select'
import { setSearchResults } from '../../../redux/actions/violations'
import { startCase } from 'lodash'

import { defaultFilter, loadingAreas, statusFilterDetails, statusTypes } from '../../../utils/variables'
import { formatUserDisplayName } from '../../../utils/functions/violationFunctions'

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: 'rgba(0, 0, 0, 0.54)',
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 14,
    leavTouchDelay: 10000
  }
}))(Tooltip)

const useStyles = makeStyles((theme) => ({
  backBtn: {
    backgroundColor: '#8BB84B',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#588518'
    }
  },
  deleteBtn: {
    backgroundColor: theme.palette.action.active,
    margin: 'auto',
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.light
    }
  },
  greenBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#8AB651'
    },
    borderRadius: '50%'
  },
  blueBtn: {
    backgroundColor: '#3984f7',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  greenText: {
    color: '#97b557'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  listHeader: {
    fontWeight: 'bold',
    wordBreak: 'break-word',
    fontSize: '1.5rem',
    color: '#495057'
  },
  problem: {
    backgroundColor: '#FFA593',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#ef8c78'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  viewAll: {
    backgroundColor: '#7eb9e9',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#4d9fe1'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  unviewed: {
    backgroundColor: '#CB9BFF',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#a586c7'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  hold: {
    backgroundColor: '#FFFF91',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#f7f787'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  resolved: {
    backgroundColor: '#A6FFFF',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#70e8e8'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  notaproblem: {
    backgroundColor: '#96E1A8',
    color: '#616161',
    '&:hover': {
      backgroundColor: '#73cc88'
    },
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  buttonContainer: {
    borderBottom: 'solid #e0e0e0 1px',
    paddingTop: '1rem',
    paddingBottom: '1.5rem'
  },
  searchList: {
    width: '100%'
  },
  buttonHeader: {
    marginBottom: '.2rem',
    fontSize: '.9rem'
  },
  loader: {
    color: '#A4D164'
  }
}))

const searchResultLabels = {
  users: 'User',
  applications: 'Application',
  workstations: 'WorkStation',
  categories: 'Category',
  phrases: 'Phrase',
  websites: 'Website',
  station_ips: 'Internet Protocol Address'
}

/**
 * Creates a modal that displays results of global search
 * @param {func} onFilter function called in the parent component when the term to filter by is clicked
 * @param {func} toggleModal calls parent component to toggle modal visibility
 * @param {Boolean} open parent bit determining whether the component is visible or not
 * @param {Array} searchResults iterable of search results to loop through and display as clickable elements
 */
const SearchResults = (props) => {
  const { setSelectedStatus } = props

  const classes = useStyles()
  const dispatch = useDispatch()
  const { promiseInProgress } = usePromiseTracker({ area: loadingAreas.SEARCH_VIOLATIONS })

  const searchResults = useSelector(state => state.violations.searchResults)
  const { permissions = [] } = useSelector(state => state.auth)

  const redirect = () => {
    if (props.currentView === 'list') {
      props.history.push({
        pathname: '/captures',
        search: '?view=list&page=1&activeCapture=0'
      })
    } else if (props.currentView === 'grid') {
      props.history.push({
        pathname: '/captures',
        search: '?view=grid&page=1&activeCaptures=[]'
      })
    } else {
      props.history.push({
        pathname: '/captures',
        search: '?view=single&capture=1'
      })
    }
  }

  const handleToggleModal = () => {
    dispatch(setSearchModal(false))
    dispatch(setSearchResults({}))
  }

  const getCount = (capture, key) => {
    // get the bucket matching the key
    const bucket = capture.status_flag.buckets.filter((bucket) => { return bucket.key === key })
    let count = 0
    // if the bucket exists, return its doc count
    if (bucket && bucket.length > 0) { count = bucket[0].doc_count }
    return count
  }

  // handle adding the selected values to the filter (this is an attempt to combine previous, extremely duplicated logic, but most of this logic needs to be redone entirely, but is outside the scope of the current ticket to do. Can hates most of it.)
  const handleBadgeClick = (e, key, viewCaptureArea = false) => {
    e.stopPropagation()
    e.preventDefault()

    // the ID of the selected badge
    const id = e.currentTarget.id
    let captureArea = null
    let filterValue = id

    // if the badge is for a specific capture area, split the button's id value into the capture area and the filter value
    if (viewCaptureArea) {
      captureArea = id.slice(-1)
      filterValue = id.slice(0, id.length - 1)
    }

    // init an empty subFilter
    let subFilter = {}

    // yes. this is dumb, please change it when time allows.
    // but use the result section's name to determine the correct 'Filter' key, value type, and value.
    // (note that two are strings, the rest are arrays, and one result's key is the the same as the Filter key -_-)
    switch (key) {
      case 'users': { subFilter = { usernames: [filterValue] }; break }
      case 'workstation': { subFilter = { workstations: [filterValue] }; break }
      case 'category': { subFilter = { categories: [filterValue] }; break }
      case 'phrases': { subFilter = { phrase: filterValue }; break }
      case 'websites': { subFilter = { websiteUrl: filterValue }; break }
      case 'station_ips': { subFilter = { station_ips: [filterValue] }; break }
      default: { subFilter = { [key]: [filterValue] }; break }
    }

    // build the final filter value
    const filter = { ...defaultFilter, refresh: true, ...subFilter, ...(captureArea && { captureAreas: [captureArea] }) }
    // find the correct status based on if there was a desired capture area or not
    const newSelectedStatus = captureArea || statusTypes.ALL_CAPTURES

    // perform all actions and reset the modal/search area
    setSelectedStatus(newSelectedStatus)
    dispatch(setFilter(filter))
    redirect()
    handleToggleModal()
    props.setSearchOpen(false)
  }
  const truncateText = (input) => {
    if (input.length > 45) {
      return input.substring(0, 45) + '...'
    }
    return input
  }

  // determine if a status type should be disabled
  const statusIsDisabled = (statusKey) => {
    const statusObj = statusFilterDetails.find(x => x.statusValue === statusKey)
    const permissionKey = statusObj?.statusPermission
    return Boolean(permissions?.length && permissionKey && !permissions.includes(permissionKey))
  }

  // generates a button badge for each capture area within a result
  const generateButtons = (result, key) => {
    // find any status types that have results within the dataset
    const relevantStatusObjs = statusFilterDetails.filter(x => getCount(result, x.statusValue) !== 0)
    return (
      <>
        {relevantStatusObjs.map(statusObj => {
          const { statusValue, statusLabel } = statusObj
          const disabledOption = statusIsDisabled(statusValue)
          const className = statusLabel.replace(/\s/g, '').toLowerCase()
          return (
            <Grid key={`${key}-button-${statusValue}`} item container direction='column' xs={2} className='mx-1'>
              <Grid item container direction='row' justify='center'>
                <Typography className={classes.buttonHeader}>{statusLabel}</Typography>
              </Grid>
              <Grid item container direction='row' justify='center'>
                <LightTooltip enterDelay={1200} enterNextDelay={1200} title={disabledOption ? 'Your user role does not have access to view this status.' : `Filter By "${statusLabel}"`} placement='bottom' arrow>
                  <span>
                    <Button
                      id={result.key + statusValue}
                      className={classes[className]}
                      onClick={(e) => { handleBadgeClick(e, key, true) }}
                      disabled={disabledOption}
                    >
                      {getCount(result, statusValue, disabledOption)}
                    </Button>
                  </span>
                </LightTooltip>
              </Grid>
            </Grid>
          )
        })}
      </>
    )
  }

  const PopulateResultsList = (results) => {
    if (promiseInProgress) {
      return (
        <CircularProgress className={classes.loader} />
      )
    }

    const hasResults = Boolean(results && Object.keys(results).length && Object.values(results).some(valArr => Boolean(valArr.length)))

    if (hasResults) {
      const resMap = Object.entries(results).map(([resKey, resValues]) => {
        if (Array.isArray(resValues)) {
          return resValues.map((result, index) => {
            const displayName = resKey === 'users' && result?.fullName ? formatUserDisplayName({ fullName: result.fullName, username: result.key }) : result.key
            return (
              <ListItem
                id={result.key}
                className={classes.buttonContainer}
                key={`${resKey}-${index}`}
              >
                <Grid item container direction='column' xs={12}>
                  <Grid item container direction='row'>
                    <LightTooltip title={result.key} placement='top-end' arrow>
                      <Typography className={classes.listHeader}>
                        {truncateText(displayName)}
                      </Typography>
                    </LightTooltip>
                  </Grid>
                  <Grid item container direction='row'>
                    {/* Use the corresponding result label or, as a fallback, capitalize the key */}
                    <Typography>{searchResultLabels[resKey] || startCase(resKey)}</Typography>
                  </Grid>
                  <Grid item container direction='row' justify='flex-start' className='mt-3 ml-2'>
                    <Grid key={`${resKey}-button-all`} item container direction='column' xs={2} className='mx-1'>
                      <Grid item container direction='row' justify='center'>
                        <Typography className={classes.buttonHeader}>View All</Typography>
                      </Grid>
                      <Grid item container direction='row' justify='center'>
                        <Button
                          className={classes.viewAll}
                          onClick={(e) => { handleBadgeClick(e, resKey) }}
                          id={result.key}
                        >
                          {result?.doc_count || 0}
                        </Button>
                      </Grid>
                    </Grid>
                    {generateButtons(result, resKey)}
                  </Grid>
                </Grid>
              </ListItem>
            )
          })
        }
        return false
      })
      return (
        <List className={classes.searchList}>
          {resMap}
        </List>
      )
    }

    return (
      <Paper
        className='w-100 mx-auto'
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '10rem'
        }}
        elevation={3}
      >
        <Grid item container direction='column' sm={12}>
          <Grid item container direction='row' justify='center'>
            <Typography
              className='unselectable'
              style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#5a5a5ade' }}
            >
              No results returned.
            </Typography>
          </Grid>
          <Grid item container direction='row' justify='center'>
            <Typography className='unselectable'>Try optimizing your search query, or check for spelling errors.</Typography>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  return (
    <>
      <Dialog open={props.open} onClose={handleToggleModal} className='filterModal' fullWidth maxWidth='md'>
        <MuiDialogTitle disableTypography>
          <Typography
            variant='h4'
            color='primary'
            classes={{ colorPrimary: classes.greenText }}
            className='font-weight-bold unselectable'
          >
            Search Results
          </Typography>
          <IconButton aria-label='close' className={classes.closeButton} onClick={handleToggleModal}>
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <MuiDialogContent dividers>
          <Grid item container direction='row' justify='center'>
            {PopulateResultsList(searchResults)}
          </Grid>
        </MuiDialogContent>
      </Dialog>
    </>
  )
}

export default withRouter(SearchResults)
